if (!customElements.get('armada-slide-gallery')) {
  class ArmadaSlideGallery extends HTMLElement {
    constructor() {
      super();
      this.isPlaying = true;
      this.isVisible = false;
      this.progress = 0;
      this.currentIndex = 0;
      this.animationFrame = null;
      this._suppressScrollSync = false;
      this._scrollSyncTimer = null;
      this._scrollSettleHandler = null;
      this.duration = (parseInt(this.dataset.autoplaySpeed) || 5) * 1000;
    }

    connectedCallback() {
      this.track = this.querySelector('.armada-slide-gallery__track-wrapper');
      this.cards = this.querySelectorAll('.armada-slide-gallery__card');
      this.playPauseBtn = this.querySelector('.armada-slide-gallery__play-pause');
      this.dots = this.querySelectorAll('.armada-slide-gallery__dot');
      this.dotFills = this.querySelectorAll('.armada-slide-gallery__dot-fill');
      this.iconPause = this.querySelector('.armada-slide-gallery__icon-pause');
      this.iconPlay = this.querySelector('.armada-slide-gallery__icon-play');

      this.querySelectorAll('video').forEach((v) => {
        v.autoplay = false;
        v.pause();
      });
      requestAnimationFrame(() => this.pauseAllVideos());

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.isPlaying = false;
        this.updatePlayPauseIcon();
      }

      this.setupEventListeners();
      this.updateActiveDot();
      this.observeVisibility();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.animationFrame);
      clearTimeout(this._scrollSyncTimer);
      if (this._scrollSettleHandler) {
        this.track.removeEventListener('scroll', this._scrollSettleHandler);
        this._scrollSettleHandler = null;
      }
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
    }

    observeVisibility() {
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;
            if (entry.isIntersecting) {
              if (this.isPlaying) {
                this.startProgress();
                this.playActiveVideo();
              }
            } else {
              cancelAnimationFrame(this.animationFrame);
              this.pauseAllVideos();
            }
          });
        },
        { threshold: 0.15 }
      );
      this.visibilityObserver.observe(this);
    }

    _getScrollPaddingLeft() {
      return parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    }

    _snapTargetFor(index) {
      const card = this.cards[index];
      if (!card) return 0;
      return Math.max(0, card.offsetLeft - this._getScrollPaddingLeft());
    }

    _closestIndexFromScroll() {
      let closestIndex = 0;
      let minDiff = Infinity;
      this.cards.forEach((card, index) => {
        const diff = Math.abs(this._snapTargetFor(index) - this.track.scrollLeft);
        if (diff < minDiff) {
          minDiff = diff;
          closestIndex = index;
        }
      });
      return closestIndex;
    }

    playActiveVideo() {
      if (!this.isVisible || !this.isPlaying) return;
      const activeCard = this.cards[this.currentIndex];
      if (!activeCard) return;
      const video = activeCard.querySelector('video');
      if (video && video.paused) video.play();
      this.querySelectorAll('video').forEach((v) => {
        if (v !== video && !v.paused) v.pause();
      });
    }

    pauseAllVideos() {
      this.querySelectorAll('video').forEach((v) => {
        if (!v.paused) v.pause();
      });
    }

    setupEventListeners() {
      this.playPauseBtn.addEventListener('click', () => {
        if (this.isPlaying) {
          this.pause();
        } else {
          if (this.currentIndex >= this.cards.length - 1) {
            this.resetProgress();
            this.goToSlide(0);
          }
          this.play();
        }
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index);
          this.resetProgress();
          this.goToSlide(index);
        });
      });

      this.track.addEventListener(
        'scroll',
        () => {
          if (this._suppressScrollSync) return;
          const idx = this._closestIndexFromScroll();
          if (idx !== this.currentIndex) {
            this.currentIndex = idx;
            this._setActiveDot(idx);
            this.resetProgress();
            if (this.isPlaying) this.startProgress();
          }
        },
        { passive: true }
      );
    }

    play() {
      this.isPlaying = true;
      this.updatePlayPauseIcon();
      this.playActiveVideo();
      this.startProgress();
    }

    pause() {
      this.isPlaying = false;
      cancelAnimationFrame(this.animationFrame);
      this.updatePlayPauseIcon();
      this.pauseAllVideos();
    }

    updatePlayPauseIcon() {
      if (this.isPlaying) {
        this.iconPause.classList.remove('hidden');
        this.iconPlay.classList.add('hidden');
        this.playPauseBtn.setAttribute('aria-label', 'Pause slideshow');
      } else {
        this.iconPause.classList.add('hidden');
        this.iconPlay.classList.remove('hidden');
        this.playPauseBtn.setAttribute('aria-label', 'Play slideshow');
      }
    }

    startProgress() {
      cancelAnimationFrame(this.animationFrame);
      let lastTime = performance.now();

      const animate = (currentTime) => {
        if (!this.isPlaying) return;

        const deltaTime = currentTime - lastTime;
        lastTime = currentTime;

        this.progress += deltaTime;

        if (this.progress >= this.duration) {
          this.resetProgress();
          this.nextSlide();
          return;
        }

        const percentage = Math.min((this.progress / this.duration) * 100, 100);
        const fill = this.dotFills[this.currentIndex];
        if (fill) fill.style.width = `${percentage}%`;

        this.animationFrame = requestAnimationFrame(animate);
      };

      this.animationFrame = requestAnimationFrame(animate);
    }

    resetProgress() {
      this.progress = 0;
      this.dotFills.forEach((fill) => {
        fill.style.width = '0%';
      });
    }

    nextSlide() {
      if (this.currentIndex >= this.cards.length - 1) {
        this.pause();
        return;
      }
      this.goToSlide(this.currentIndex + 1);
      if (this.isPlaying) this.startProgress();
    }

    goToSlide(index) {
      this.currentIndex = index;
      this._setActiveDot(index);
      this._programmaticScrollTo(index);
    }

    _programmaticScrollTo(index) {
      const card = this.cards[index];
      if (!card) return;

      this._suppressScrollSync = true;
      clearTimeout(this._scrollSyncTimer);
      if (this._scrollSettleHandler) {
        this.track.removeEventListener('scroll', this._scrollSettleHandler);
      }

      this.track.scrollTo({ left: this._snapTargetFor(index), behavior: 'smooth' });

      // Release the sync only once the smooth scroll has actually stopped, rather
      // than after a fixed guess. A 600ms timeout expired mid-flight on long
      // scrolls (a card is ~992px), so the scroll handler re-synced to whichever
      // card happened to be closest at that moment: it jumped the active dot and
      // ran pauseAllVideos()/playActiveVideo(), which stuttered the video and
      // reflowed the dots row. Debouncing on the scroll itself is duration-proof.
      this._scrollSettleHandler = () => {
        clearTimeout(this._scrollSyncTimer);
        this._scrollSyncTimer = setTimeout(() => {
          this.track.removeEventListener('scroll', this._scrollSettleHandler);
          this._scrollSettleHandler = null;
          this._suppressScrollSync = false;
        }, 120);
      };

      this.track.addEventListener('scroll', this._scrollSettleHandler, { passive: true });
      // Kick it off so the guard still releases if no scroll event ever fires
      // (e.g. we're already at the target).
      this._scrollSettleHandler();
    }

    _setActiveDot(index) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        if (i !== index) this.dotFills[i].style.width = '0%';
      });
      this.pauseAllVideos();
      this.playActiveVideo();
    }

    updateActiveDot() {
      this._setActiveDot(this.currentIndex);
    }
  }

  customElements.define('armada-slide-gallery', ArmadaSlideGallery);
}
